// HB-1948: Shared upload helper. Wraps `api.dtf.uploadArtwork`, forces
// `silent: true` on the underlying axiosRequest so HB auto-notify is
// suppressed, then on rejection calls `logUploadErrorEvent(pageInfo, file,
// error)` and re-throws so per-surface UI handling (banners, alerts, store
// mutations) still fires.
//
// Migration status: 8 of 10 `api.dtf.uploadArtwork` callers route through
// this helper as of HB-1948. The 2 remaining direct callers
// (`product-card-transfers.js`, `react-product-details-dtf/store.ts`) already
// invoke `logUploadErrorEvent` in their catch blocks, so their Mixpanel
// coverage is correct — but they still skip `silent: true`, so a small
// residual HB-422 leak from those 2 surfaces remains. Migrating them is a
// follow-up because the move requires removing their existing catch-block
// `logUploadErrorEvent` calls to avoid double-fire.
//
// Why migrating fixes the leak:
//   1. Validation 422s no longer leak to Honeybadger as noise — `silent: true`
//      tells `axiosRequest` to skip the auto-notify, and `logImageUploadError`
//      then short-circuits on EXPECTED_HTTP_STATUSES so the typed Mixpanel
//      event fires without an HB notice.
//   2. The typed Mixpanel error events (`image_validation_error`,
//      `image_upload_rejected_validation`, `image_upload_process_error`)
//      fire consistently across the migrated upload surface — not just the
//      dropzone path.
//
// `pageInfo` is intentionally a small TS type — `dropzone-error-event-tracker.ts`
// reads `pageValue`, `businessLine`, and the optional `orderType` — surfacing
// shape divergence at compile time so future callers don't drop fields.

import { api } from "jiffy-api";
import { logUploadErrorEvent } from "common/utils/mixpanel/dropzone-error-event-tracker";
import { notifyError } from "common/utils/app-monitoring/notify";

export type PageInfo = {
  pageValue: string;
  businessLine?: string | null;
  orderType?: string;
};

type UploadArtworkResponse = Awaited<ReturnType<typeof api.dtf.uploadArtwork>>;

// `api.dtf.uploadArtwork`'s second arg comes from the auto-generated API
// types as `unknown`, which TS won't spread. Re-declare as an open record
// — the axiosRequest layer accepts any superset including the request-layer
// `silent` flag added below.
type UploadArtworkOptions = Record<string, unknown>;

// The dropzone tracker reads `file.name`, `file.type`, `file.size`,
// `file.width`, `file.height`. FormData.get("file") returns a `File | string |
// null`; we narrow it and fall back to a minimal stub so the logger can still
// build a Mixpanel payload when callers attach the file under a different
// key (e.g. legacy "design[file]") or omit it entirely.
const extractFileForLogging = (formData: FormData): Record<string, unknown> => {
  const value = formData.get("file");
  if (value instanceof Blob) {
    const file = value as File;
    return {
      name: file.name || "unknown",
      type: file.type || "",
      size: file.size ?? 0,
      // width/height aren't part of the File API; dropzone callers populate
      // them as ad-hoc properties on the File object before upload. Forward
      // whatever's there, default to 0 so `.toFixed(2)` doesn't blow up.
      width: (file as unknown as { width?: number }).width ?? 0,
      height: (file as unknown as { height?: number }).height ?? 0
    };
  }
  return { name: "unknown", type: "", size: 0, width: 0, height: 0 };
};

export const uploadArtworkWithTracking = async (
  formData: FormData,
  pageInfo: PageInfo,
  options: UploadArtworkOptions
): Promise<UploadArtworkResponse> => {
  // HB-1948: force `silent: true` so the underlying request layer
  // (`axiosRequest` in `frontend/common/utils/request/request.js`) does NOT
  // auto-notify Honeybadger on rejection. The helper centralizes upload-error
  // reporting via `logUploadErrorEvent`, which short-circuits on
  // EXPECTED_HTTP_STATUSES (401/403/404/422/502/503/504). Without `silent: true`
  // here, validation 422s reach `reportRequestError` BEFORE control returns to
  // our `catch` block — defeating the whole point of routing through this helper.
  // Helper owns reporting; caller-provided `silent` is intentionally overridden.
  const silencedOptions = { ...options, silent: true };
  try {
    return await api.dtf.uploadArtwork(formData, silencedOptions);
  } catch (error) {
    // Defensive: tracking is a side-effect; never let a logger bug compound
    // the original upload failure. If `logUploadErrorEvent` itself throws
    // (e.g., an unexpected response shape inside `getErrorContext`), report
    // the secondary failure to Honeybadger and still re-throw the original.
    try {
      const file = extractFileForLogging(formData);
      logUploadErrorEvent(pageInfo, file, error);
    } catch (loggerError) {
      notifyError("uploadArtworkWithTracking logger failed", {
        params: {
          loggerError: {
            message: (loggerError as Error)?.message,
            name: (loggerError as Error)?.name
          }
        }
      });
    }
    throw error;
  }
};
